import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import ProductCard from '../product/ProductCard';
import { search } from '@api/search';
import ErrorBoundary from '../error/ErrorBoundary';

function ManufacturerPage() {
  const { manufacturerName } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numFound, setNumFound] = useState(0);

  // Display name: decode and prettify
  const displayManufacturerName = manufacturerName
    ? decodeURIComponent(manufacturerName).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : '';

  useEffect(() => {
    const fetchManufacturerProducts = async () => {
      try {
        // Use the exact value from the URL for the facet/manufacturer filter
        const fqValue = decodeURIComponent(manufacturerName);
        const response = await search({
          query: '*:*',
          filterQueries: [`manufacturer:("${fqValue}")`],
          rows: 24,
          start: 0,
          facet: false,
          queryProfile: 'default', // Use default instead of landing-page for manufacturer pages
          // Only request essential fields
          fields: 'id,title,price,price_d,price_f,image_url_s,image_url,description,manufacturer,category,sku,variant_images,variants,total_variants',
          // Add sort for consistent results
          sort: 'score desc,id asc'
        });
        if (response && response.data && response.data.response && response.data.response.docs) {
          setProducts(response.data.response.docs);
          setNumFound(response.data.response.numFound);
        } else {
          setProducts([]);
          setNumFound(0);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch products for this manufacturer.');
        setProducts([]);
        setNumFound(0);
      } finally {
        setLoading(false);
      }
    };
    fetchManufacturerProducts();
  }, [manufacturerName]);

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading products for {displayManufacturerName}...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          Error loading products for {displayManufacturerName}: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {displayManufacturerName} Products
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        {numFound} product{numFound !== 1 ? 's' : ''} found
      </Typography>
      <Grid container spacing={3}>
        {products.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">No products found for this manufacturer.</Alert>
          </Grid>
        ) : (
          products.map((product, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id ? String(product.id) : product.sku ? `sku-${product.sku}` : `product-${idx}`}>
              <ErrorBoundary>
                <ProductCard product={product} />
              </ErrorBoundary>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default ManufacturerPage;
